import { inject } from '@angular/core';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';

import { GqlService, listPatientProtocolVersionsQuery } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { batchAPIRequest } from '@shared/utils/batch-promises';
import { gqlToPromise } from '@shared/utils/gql-to-promise';

export const injectProtocolVersions = () => {
  const gqlService = inject(GqlService);
  const mainQuery = inject(MainQuery);
  const overlayService = inject(OverlayService);

  return injectQuery<listPatientProtocolVersionsQuery[], string[]>(() => ({
    onError: (error: string[]) => {
      overlayService.error(error);
    },
    queryFn: async () => {
      const response = await gqlToPromise(() => gqlService.listPatientProtocolVersions$());

      return response as listPatientProtocolVersionsQuery[];
    },
    queryKey: ['patientProtocolVersions', mainQuery.trialKey()],
  }));
};

export const injectPatientProtocolSubTypes = () => {
  const gqlService = inject(GqlService);
  const mainQuery = inject(MainQuery);

  return injectQuery(() => ({
    queryFn: () => {
      return gqlToPromise(() => gqlService.listPatientProtocolSubTypes$([]));
    },
    queryKey: ['site', 'patient-protocol-sub-types', mainQuery.trialKey()],
  }));
};

export const injectBatchRemovePatientProtocols = (silent = false) => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);

  return injectMutation(() => ({
    mutationFn: async ({ patientProtocolIds }: { patientProtocolIds: string[] }) => {
      const { data, errors } = await batchAPIRequest(
        patientProtocolIds,
        (input) => gqlService.batchRemovePatientProtocols$(input),
        50
      );

      if (errors.length) {
        throw errors;
      }

      return data;
    },
    onError: (error: string[]) => {
      overlayService.error(error);
    },
    onSuccess: () => {
      if (!silent) {
        overlayService.success('Successfully deleted');
      }
    },
  }));
};
